import { useEffect, useState } from "react";
import { Loader2, Check, Sparkles, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SOLD_OUT, isComingSoon } from "@/lib/storeStatus";
import { fetchProductByHandle, type ShopifyProduct } from "@/lib/shopify";
import { computeBundlePricing } from "@/lib/bundles";
import { NO_COST_EMI_MIN_INR } from "@/lib/checkout-pricing";
import { useCartStore } from "@/stores/cartStore";
import heroMag__asset from "@/assets/product-hero/mag-absorb-hero.png.asset.json";
const heroMag = heroMag__asset.url;
import heroOva__asset from "@/assets/product-hero/ova-balance-hero.png.asset.json";
const heroOva = heroOva__asset.url;
import heroLiver__asset from "@/assets/liver-biome-v2/01-hero.webp.asset.json";
const heroLiver = heroLiver__asset.url;

type P = ShopifyProduct["node"];

const PROTOCOL_HANDLES = ["liver-biome-plus", "mag-absorb-pro", "ova-balance"] as const;

const HERO_BY_HANDLE: Record<string, string> = {
  "liver-biome-plus": heroLiver,
  "mag-absorb-pro": heroMag,
  "ova-balance": heroOva,
};

export function ProtocolBundle({ currentHandle }: { currentHandle?: string }) {
  const [products, setProducts] = useState<P[]>([]);
  const [addingHandle, setAddingHandle] = useState<string | null>(null);
  const [addingAll, setAddingAll] = useState(false);
  const [addedHandle, setAddedHandle] = useState<string | null>(null);
  const addItems = useCartStore((s) => s.addItems);

  useEffect(() => {
    let cancelled = false;
    Promise.all(PROTOCOL_HANDLES.map((h) => fetchProductByHandle(h))).then((rs) => {
      if (cancelled) return;
      setProducts(rs.filter((r): r is P => !!r));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (products.length < 3) return null;

  // Coming-soon SKUs can be shown, but never added to the cart.
  const buyable = products.filter((p) => !isComingSoon(p.handle));

  // Bundle math over the products that can actually be bought today.
  const trio = computeBundlePricing(
    buyable.map((p) => parseFloat(p.variants.edges[0]?.node.price.amount ?? "0")),
  );


  function toLine(p: P, qty = 1) {
    const v = p.variants.edges[0]?.node;
    if (!v) return null;
    return {
      variantId: v.id,
      productHandle: p.handle,
      productTitle: p.title,
      imageUrl: p.images.edges[0]?.node.url ?? null,
      price: v.price,
      quantity: qty,
    };
  }

  async function handleAddOne(p: P) {
    if (isComingSoon(p.handle)) return;

    const line = toLine(p);
    if (!line) return;
    setAddingHandle(p.handle);
    await addItems([line]);
    setAddingHandle(null);
    setAddedHandle(p.handle);
    (window as unknown as { __openCart?: () => void }).__openCart?.();
    setTimeout(() => setAddedHandle((h) => (h === p.handle ? null : h)), 1600);
  }

  async function handleAddAll() {
    const lines = buyable
      .map((p) => toLine(p))
      .filter((l): l is NonNullable<typeof l> => !!l);
    if (lines.length === 0) return;
    setAddingAll(true);
    await addItems(lines);
    setAddingAll(false);
    (window as unknown as { __openCart?: () => void }).__openCart?.();
  }

  return (
    <section
      data-testid="protocol-bundle"
      className="mt-10 md:mt-20"
      aria-labelledby="protocol-bundle-title"
    >
      <div className="mx-auto max-w-3xl text-center">
        <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-brand">
          <Sparkles className="h-3 w-3" /> The TruWe Protocol
        </p>
        <h2
          id="protocol-bundle-title"
          className="mt-2 font-display text-xl font-bold text-brand-deep md:text-4xl"
        >
          Designed to Work Together
        </h2>
        <p className="mt-2 text-xs text-muted-foreground md:text-base">
          Three separate formulas with no duplicated actives, so they can be taken together:
          Liver Biome Plus for liver and gut, Mag Absorb Pro for sleep and recovery, Ova Balance
          for cycle and hormone support. All three save {Math.round(trio.rate * 100)}%, applied
          automatically in your cart.
        </p>
        <p className="mx-auto mt-2 max-w-xl text-[11px] leading-relaxed text-muted-foreground/80 md:text-xs">
          Ova Balance is formulated for women managing PCOS or irregular cycles — if that isn't
          you, add only the products that match your concern.
        </p>

      </div>

      <div className="mx-auto mt-5 grid max-w-5xl grid-cols-3 gap-2 sm:mt-8 sm:gap-4">
        {products.map((p) => {
          const isCurrent = currentHandle && p.handle === currentHandle;
          const img = HERO_BY_HANDLE[p.handle] ?? p.images.edges[0]?.node.url;
          const price = parseFloat(p.variants.edges[0]?.node.price.amount ?? "0");
          const adding = addingHandle === p.handle;
          const added = addedHandle === p.handle;
          const comingSoon = isComingSoon(p.handle);
          return (
            <div
              key={p.id}
              data-testid="protocol-bundle-card"
              className={`flex flex-col overflow-hidden rounded-xl border bg-card p-2.5 text-center transition sm:rounded-2xl sm:p-5 ${
                isCurrent ? "border-brand/40 ring-1 ring-brand/20" : "border-border"
              }`}
            >
              <div className="mx-auto h-16 w-16 overflow-hidden rounded-lg bg-brand-soft sm:h-28 sm:w-28 sm:rounded-xl">
                {img && (
                  <img
                    src={img}
                    alt={p.title}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                )}
              </div>
              <h3 className="mt-2 font-display text-[11px] font-semibold leading-tight text-brand-deep sm:mt-4 sm:text-base">
                {p.title}
              </h3>
              <p className="mt-0.5 text-[10px] text-muted-foreground sm:mt-1 sm:text-xs">₹{price.toFixed(0)}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 h-8 rounded-full border-foreground/15 px-2 text-[10px] font-semibold sm:mt-4 sm:h-10 sm:text-xs"
                disabled={adding || SOLD_OUT || comingSoon}
                onClick={() => handleAddOne(p)}
              >
                {comingSoon ? (
                  "Coming soon"
                ) : SOLD_OUT ? (
                  "Sold out"
                ) : adding ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : added ? (
                  <span className="inline-flex items-center gap-1">
                    <Check className="h-3 w-3" /> Added
                  </span>
                ) : (
                  "Add"
                )}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="mx-auto mt-6 flex max-w-5xl flex-col items-center gap-3">
        {trio.savings > 0 && (
          <div className="flex flex-wrap items-baseline justify-center gap-x-2.5">
            <span className="text-lg font-semibold tracking-tight text-brand-deep">
              ₹{trio.total.toLocaleString("en-IN")}
            </span>
            <span className="text-sm text-muted-foreground line-through">
              ₹{trio.subtotal.toLocaleString("en-IN")}
            </span>
            <span className="rounded-full bg-brand/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-brand-deep">
              Save ₹{trio.savings.toLocaleString("en-IN")}
            </span>
          </div>
        )}
        {trio.total >= NO_COST_EMI_MIN_INR && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft/70 px-3 py-1 text-[11px] font-semibold text-brand-deep ring-1 ring-brand/20">
            <CreditCard className="h-3 w-3" /> No-cost EMI available on this bundle
          </span>
        )}
        {buyable.length >= 2 && (
          <Button
            data-testid="protocol-bundle-add-all"
            size="lg"
            className="h-12 rounded-full bg-brand-deep px-8 text-sm font-semibold text-white hover:bg-brand-deep/90"
            disabled={addingAll || SOLD_OUT}
            onClick={handleAddAll}
          >
            {SOLD_OUT ? (
              "Sold out"
            ) : addingAll ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              `Add the ${buyable.length}-product protocol, save ${Math.round(trio.rate * 100)}%`
            )}
          </Button>
        )}
      </div>

    </section>
  );
}
